import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getSubscription } from '../services/api';
import { getSetting, setSetting } from '../db/sleepDB';
import { getPaymentPlatform } from '../utils/platform';

const SubscriptionContext = createContext();

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState({
    status: 'free',
    plan: null,
    source: null,
    expiresAt: null,
    trialExpiresAt: null,
    createdAt: null,
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await getSubscription();
      setSubscription(data);
      await setSetting('subscription', data);
    } catch {
      // Offline or error — keep current state
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let mounted = true;

    async function load() {
      // Load cached first
      try {
        const cached = await getSetting('subscription');
        if (cached && mounted) {
          setSubscription(cached);
        }
      } catch {
        // No cache
      }

      // Then fetch fresh
      try {
        const data = await getSubscription();
        if (mounted) {
          setSubscription(data);
          await setSetting('subscription', data);
        }
      } catch {
        // Offline — cached value remains
      }

      if (mounted) setLoading(false);
    }

    load();
    return () => { mounted = false; };
  }, [isAuthenticated]);

  // Apple IAP: the `verified` handler refreshes the subscription once its
  // verify-receipt writes settle. The paywall sheet has closed by then, so the
  // hook lives here, on the provider that outlives it.
  useEffect(() => {
    if (getPaymentPlatform() !== 'apple') return undefined;
    let cancelled = false;
    let detach = () => {};
    import('../services/iap').then(({ setOnSubscriptionRefresh }) => {
      if (cancelled) return;
      setOnSubscriptionRefresh(refresh);
      detach = () => { setOnSubscriptionRefresh(null); };
    });
    return () => { cancelled = true; detach(); };
  }, [refresh]);

  const isPremium = subscription.status === 'premium' || subscription.status === 'trial';

  const accountAgeDays = subscription.createdAt
    ? Math.floor((Date.now() - new Date(subscription.createdAt).getTime()) / 86400000)
    : 0;

  return (
    <SubscriptionContext.Provider value={{
      ...subscription,
      isPremium,
      accountAgeDays,
      paymentPlatform: getPaymentPlatform(),
      loading,
      refresh,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export const useSubscription = () => useContext(SubscriptionContext);
